import { useState, useMemo } from "react";
import api from "@/lib/api";
import { eur, fmtDate, parseAmount, toISO } from "@/lib/format";
import { getPeriodRange, inRange } from "@/lib/periods";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileDown, FileSpreadsheet, X } from "lucide-react";

const CATEGORIES = ["Achats marchandises", "Fournitures", "Frais généraux", "Personnel", "Autre"];

export default function ExpensesManager({ expenses, reload, period, custom }) {
  const range = useMemo(() => getPeriodRange(period, new Date(), custom), [period, custom]);

  const [date, setDate] = useState(toISO(new Date()));
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const rows = useMemo(
    () =>
      expenses
        .filter((e) => inRange(e.date, range.start, range.end))
        .sort((a, b) => b.date.localeCompare(a.date)),
    [expenses, range]
  );
  const total = rows.reduce((a, b) => a + (b.amount || 0), 0);

  const resetForm = () => {
    setEditId(null);
    setLabel("");
    setAmount("");
    setCategory(CATEGORIES[0]);
    setDate(toISO(new Date()));
  };

  const save = async () => {
    if (parseAmount(amount) <= 0) {
      toast.error("Indiquez un montant");
      return;
    }
    setBusy(true);
    try {
      const payload = { date, label: label.trim(), amount: parseAmount(amount), category };
      if (editId) {
        await api.put(`/expenses/${editId}`, payload);
        toast.success("Dépense mise à jour");
      } else {
        await api.post("/expenses", payload);
        toast.success("Dépense ajoutée", { description: `${category} — ${eur(parseAmount(amount))}` });
      }
      resetForm();
      await reload();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setDate(e.date);
    setLabel(e.label || "");
    setAmount(String(e.amount).replace(".", ","));
    setCategory(e.category || CATEGORIES[0]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/expenses/${deleteTarget.id}`);
      toast.success("Dépense supprimée");
      setDeleteTarget(null);
      if (editId === deleteTarget.id) resetForm();
      await reload();
    } catch {
      toast.error("Erreur de suppression");
    } finally {
      setBusy(false);
    }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/export/expenses/${fmt}`, {
        params: { start: range.start, end: range.end },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `depenses_lekker_${range.start}_${range.end}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch {
      toast.error("Échec de l'export");
    }
  };

  return (
    <div className="max-w-4xl mx-auto vlf-fade-up" data-testid="expenses-manager">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Dépenses en cash</h2>
          <p className="text-sm text-[#94A3B8]">Votre livre de dépenses — {range.label}</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-expenses-csv-button"
            onClick={() => download("csv")}
            className="h-11 px-4 rounded-xl bg-[#1A222C] border border-[#253242] text-white text-sm font-semibold flex items-center gap-2 hover:border-[#3A4D66] active:scale-[0.98] transition-all"
          >
            <FileDown className="w-4 h-4" /> CSV
          </button>
          <button
            data-testid="export-expenses-excel-button"
            onClick={() => download("xlsx")}
            className="h-11 px-4 rounded-xl bg-[#10B981] hover:bg-[#0e9d70] text-white text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-all"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      {/* Add / edit form */}
      <div className="rounded-2xl bg-[#131920] border border-[#253242] p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF5A1F]">
            {editId ? "Modifier la dépense" : "Nouvelle dépense"}
          </h3>
          {editId && (
            <button onClick={resetForm} className="text-[#94A3B8] hover:text-white flex items-center gap-1 text-sm">
              <X className="w-4 h-4" /> Annuler
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#94A3B8] mb-1">Date</label>
            <input
              data-testid="expense-date-input"
              type="date"
              value={date}
              max={toISO(new Date())}
              onChange={(e) => setDate(e.target.value)}
              className="w-full h-12 px-3 rounded-lg bg-[#0B0F12] border border-[#253242] font-mono text-white outline-none focus:border-[#FF5A1F]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#94A3B8] mb-1">Montant (€)</label>
            <input
              data-testid="expense-amount-input"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
              placeholder="0,00"
              className="w-full h-12 px-3 rounded-lg bg-[#0B0F12] border border-[#253242] font-mono text-white outline-none focus:border-[#FF5A1F]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#94A3B8] mb-1">Catégorie</label>
            <select
              data-testid="expense-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full h-12 px-3 rounded-lg bg-[#0B0F12] border border-[#253242] text-white outline-none focus:border-[#FF5A1F]"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#94A3B8] mb-1">Libellé (facultatif)</label>
            <input
              data-testid="expense-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex : viande, sauces, gaz…"
              className="w-full h-12 px-3 rounded-lg bg-[#0B0F12] border border-[#253242] text-white outline-none focus:border-[#FF5A1F]"
            />
          </div>
        </div>
        <button
          data-testid="save-expense-button"
          onClick={save}
          disabled={busy}
          className="h-13 w-full mt-3 py-3 rounded-xl bg-[#FF5A1F] hover:bg-[#E04810] text-white font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50"
        >
          <Plus className="w-5 h-5" /> {editId ? "Mettre à jour" : "Ajouter la dépense"}
        </button>
      </div>

      {/* Total */}
      <div className="rounded-xl bg-[#EF4444]/12 border border-[#EF4444]/40 p-4 mb-4 flex items-center justify-between">
        <span className="text-sm font-bold uppercase tracking-wider text-[#EF4444]">Total dépenses cash — {range.label}</span>
        <span data-testid="expenses-total" className="font-mono text-2xl font-black text-white">{eur(total)}</span>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-[#253242] overflow-hidden bg-[#131920]">
        <div className="overflow-x-auto">
          <table data-testid="expenses-table" className="w-full min-w-[560px]">
            <thead className="bg-[#0B0F12] border-b border-[#253242]">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Date</th>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Catégorie</th>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Libellé</th>
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Montant</th>
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-[#1c2530] hover:bg-[#1A222C]/60 transition-colors">
                  <td className="px-3 py-3 text-sm font-medium text-white whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-3 text-sm text-[#94A3B8]">{e.category}</td>
                  <td className="px-3 py-3 text-sm text-[#94A3B8]">{e.label || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm font-bold text-[#EF4444]">{eur(e.amount)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`edit-expense-btn-${e.id}`}
                        onClick={() => startEdit(e)}
                        className="w-9 h-9 rounded-lg bg-[#222D3A] flex items-center justify-center text-[#94A3B8] hover:text-[#38BDF8] active:scale-95 transition-all"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-expense-btn-${e.id}`}
                        onClick={() => setDeleteTarget(e)}
                        className="w-9 h-9 rounded-lg bg-[#222D3A] flex items-center justify-center text-[#94A3B8] hover:text-[#EF4444] active:scale-95 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-[#64748B]">Aucune dépense sur cette période.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#131920] border-[#253242] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette dépense ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">
              {deleteTarget && `${deleteTarget.category} — ${eur(deleteTarget.amount)} le ${fmtDate(deleteTarget.date)}. Action irréversible.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1A222C] border-[#253242] text-white hover:bg-[#222D3A] hover:text-white">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-expense-button"
              onClick={confirmDelete}
              disabled={busy}
              className="bg-[#EF4444] hover:bg-[#dc2626] text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
